import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { NoAccessState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { api, onUnauthorized, queryKeys, STALE_TIME } from '@/lib/api';
import { forgetSession, TENANT_STORAGE_KEY } from '@/lib/session/session-cache';
import {
  needsTenantSync,
  resolveActiveTenant,
  sessionStatus,
  type SessionStatus,
} from '@/lib/session/tenant-choice';
import {
  MODULES,
  resolveCallsSource,
  resolveContactsSource,
  resolveLeadsSource,
  resolveSurfaces,
  type AccionDeclarada,
  type CallsSource,
  type CapabilityId,
  type ContactsSource,
  type EstadoDeAccion,
  type LeadFieldId,
  type LeadsSource,
  type LeadTabId,
  type ModuleDefinition,
  type ModuleId,
  type MotivoDeProducto,
  type MotivoSinTablero,
  type Requisito,
  type SystemId,
} from '@/lib/modules';
import type { PlatformRole } from '@/lib/api/contract';
import type { AlcanceDePermisos, Permiso } from '@/lib/catalogo-de-permisos';
import { puedeAdministrar, puedeEscribir, type QuienMira } from '@/lib/permisos';
import { resolveSystems, type Systems } from '@/lib/system-gates';
import type { Tenant, User } from '@/types';

// Re-exported from where the sign-out cleanup lives, so the key and the code
// that clears it cannot drift apart.
export { TENANT_STORAGE_KEY };

const NO_TENANTS: Tenant[] = [];

export interface Entitlements {
  tenant: Tenant;
  user: User;
  /**
   * Los servicios de Ribox que la organización contrata: telefonía, WhatsApp.
   *
   * Un piso más abajo que `modules`, y por eso está acá arriba: un módulo se
   * apaga y se enciende, un sistema decide cuáles se **pueden** encender. Sin
   * telefonía, el módulo Llamadas no es una preferencia apagada — es imposible,
   * porque detrás no hay dialer que llame.
   *
   * Siempre completo: la ausencia del campo significa «todos contratados» y se
   * resuelve al leerlo (`resolveSystems`), así que nadie aguas abajo tiene que
   * acordarse de esa regla.
   */
  systems: Systems;
  modules: Set<ModuleId>;
  capabilities: Set<CapabilityId>;
  /** Enabled definitions in sidebar order. Lo que la organización *tiene*. */
  enabledModules: ModuleDefinition[];
  /**
   * Lo que el cascarón ofrece como destino: menú lateral, buscador de comandos,
   * atajos, y a dónde manda `/`.
   *
   * Subconjunto de `enabledModules`. Los dos existen porque tener un módulo y
   * mostrarlo en el menú son decisiones distintas: un cliente puede tener
   * Bandeja —y por lo tanto la conversación en la ficha de cada lead— sin que le
   * ofrezcamos la bandeja completa como pantalla.
   *
   * Esconder no es bloquear: la ruta sigue respondiendo si alguien escribe la
   * URL, porque el módulo está encendido y el servidor entrega los datos. Para
   * bloquear de verdad se apaga el módulo (docs/DECISIONES.md §10).
   */
  navModules: ModuleDefinition[];
  /**
   * Por qué esta organización no puede tener el tablero de Leads. `null` = puede.
   *
   * Es la razón por la que `leads` puede faltar en `navModules` teniendo el
   * módulo encendido, y lo que la ruta lee para decir cuál de los dos motivos
   * es. La tercera causa —falta el embudo o sus etapas— **no está acá**: ésa es
   * configuración incompleta, se sabe recién después de pedir los embudos, y la
   * contesta la pantalla con un estado vacío (ver `motivoSinTablero`).
   */
  sinTablero: MotivoSinTablero | null;
  hasModule: (id: ModuleId) => boolean;
  /** Si la organización contrata este servicio. La pregunta de la que cuelga la de arriba. */
  hasSystem: (id: SystemId) => boolean;
  /** `navModules` preguntado de a uno: atajos, buscador de comandos. */
  showsInNav: (id: ModuleId) => boolean;
  can: (capability: CapabilityId) => boolean;
  /**
   * Si la ficha del lead dibuja esta pestaña.
   *
   * Dos condiciones, y en este orden: que haya datos —el módulo que la alimenta
   * encendido— y que no la hayamos escondido. La primera no se puede saltar con
   * un interruptor, porque el 403 lo pone el servidor.
   */
  showsLeadTab: (id: LeadTabId) => boolean;
  /**
   * Qué campos de un lead existen para esta organización.
   *
   * Aditivo: sin configuración están todos, y con configuración están sólo los
   * que la lista nombra (más el nombre, que no se apaga). No es lo mismo que
   * esconder una columna desde el menú de la tabla — un campo que no existe no
   * se puede volver a encender desde la pantalla, porque para esta organización
   * no hay nada que encender.
   *
   * **Ya cruzado con los otros dos ejes**: un campo cuyo dueño está apagado no
   * está acá. Ésa es la razón de que las ~30 superficies que preguntan por
   * `useLeadFields()` no tengan que preguntar además por el módulo — apagar
   * Empresas descuenta `company` y la columna, la fila, el subtítulo y el
   * selector del alta desaparecen por el camino que ya existía. Ver
   * `DUEÑO_DE_CAMPO` en `@/lib/modules`.
   *
   * SE PREGUNTA CON `leadFields.has(...)` y con nada más. Hubo un alias
   * —`showsLeadField`— que no llamó nunca nadie: dos formas de hacer la misma
   * pregunta son dos lugares donde arreglar la regla, y el que no se usa es el
   * que se queda viejo.
   */
  leadFields: Set<LeadFieldId>;
  /**
   * De dónde sale la lista de Contactos de esta organización.
   *
   * Lo decide la configuración y lo aplica la capa de datos —una sola vez, para
   * todas las pantallas que piden contactos—. Acá está para lo que la capa de
   * datos no puede decidir: qué controles tiene sentido dibujar.
   */
  contactsSource: ContactsSource;
  /**
   * De dónde salen las otras dos pantallas del modelo de personas.
   *
   * Se leen igual que la de Contactos y por lo mismo: la capa de datos decide de
   * dónde traer las filas, y esto es para lo que ella no puede decidir — qué
   * controles tiene sentido dibujar.
   */
  leadsSource: LeadsSource;
  callsSource: CallsSource;
  /**
   * De qué lado de Ribox está la persona, si es que está de alguno.
   *
   * Separate from `user.role`, which says what they do inside an organisation.
   * This says whether they may configure what the organisation *has* — the
   * modules, the capabilities, the plan — which a client does not decide for
   * itself.
   *
   * TRES ESTADOS Y NO UN BOOLEANO: `'super'` lo puede todo, `'support'` lee todo
   * y configura pero no escribe un registro del cliente, `null` es del cliente.
   * El booleano no sabía decir el del medio, y de ahí el cambio.
   */
  platformRole: PlatformRole;
  /**
   * Lo que quien mira puede HACER, resuelto por el servidor.
   *
   * Distinto de `capabilities`, que es lo que la ORGANIZACIÓN compró, y de
   * `role`, que es su etiqueta: esto es el cruce de las tres cosas —su preset,
   * sus tildes y el plan— y es exactamente lo que comprueba la compuerta del
   * servidor (`access.require_permission`). Llega en el snapshot de sesión y no
   * se deriva acá: una segunda derivación es cómo nace un botón que la interfaz
   * muestra y el servidor rechaza.
   */
  permisos: ReadonlySet<Permiso>;
  /** `permisos` preguntado de a uno. La forma en que lo lee una pantalla. */
  puede: (permiso: Permiso) => boolean;
  /**
   * Los dos conjuntos que necesita quien REPARTE permisos: lo que puede
   * conceder, y lo que la organización sostiene. Ver `motivoDeTildeApagado`.
   */
  alcanceDePermisos: AlcanceDePermisos;
  /**
   * **La pregunta única**: si esta superficie se dibuja para esta organización.
   *
   * Los tres ejes cruzados de una vez. `hasModule`, `can` y `leadFields` siguen
   * estando —son la misma respuesta desglosada, y hay lugares donde alcanza con
   * una— pero cuando una superficie depende de más de uno, ésta es la forma que
   * no se olvida de ninguno: `muestra({ modulo: 'companies', campo: 'company' })`.
   */
  muestra: (requisito: Requisito) => boolean;
  /** Cuál eje faltó. Para decidir y para probar; nunca para dibujar un cartel. */
  motivoDeProducto: (requisito: Requisito) => MotivoDeProducto | null;
  /**
   * Una acción con sus dos motivos separados: el de producto la borra, el del
   * registro la deja apagada con su frase. Ver `AccionDeclarada`.
   */
  accion: (accion: AccionDeclarada) => EstadoDeAccion;
}

// Re-exportado desde donde vive la regla que lo decide, para que el estado y su
// tabla de decisión no puedan separarse.
export type { SessionStatus };

export interface Session {
  status: SessionStatus;
  tenants: Tenant[];
  tenantId: string | null;
  /** Signs in and switches organisation: one operation, two readings. */
  selectTenant: (tenantId: string) => void;
  signOut: () => void;
  retry: () => void;
}

const EntitlementsContext = createContext<Entitlements | null>(null);
const SessionContext = createContext<Session | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenantId, setTenantId] = useLocalStorage<string | null>(TENANT_STORAGE_KEY, null);
  const queryClient = useQueryClient();

  // One question — "who is signed in, and where may they go?" — asked once.
  // Resolving the person from a list of every user was only ever possible
  // because the backend was in memory; a real one answers this itself.
  const sessionQuery = useQuery({
    queryKey: queryKeys.session(tenantId),
    queryFn: () => api.session.current(tenantId ?? undefined),
    staleTime: STALE_TIME.reference,
  });

  const tenants = sessionQuery.data?.tenants ?? NO_TENANTS;
  const user = sessionQuery.data?.user ?? null;
  const platformRole = sessionQuery.data?.platformRole ?? null;
  const permisosDeLaSesion = sessionQuery.data?.permisos ?? null;

  // Cuál organización vale ahora: la recordada mientras siga entre las suyas; si
  // tiene una sola, ésa; y con dos o más sin elegir, NINGUNA — que no es un
  // error sino la pregunta que contesta el selector de organización.
  //
  // Ya no cae a `user.tenantId`, la que el servidor resolvió para la sesión: con
  // dos o más organizaciones eso era entrar a la primera membresía del documento
  // sin que nadie la eligiera. Ver `session/tenant-choice.ts`.
  const activeTenantId = resolveActiveTenant(tenants, tenantId);
  const tenant = tenants.find((candidate) => candidate.id === activeTenantId) ?? null;

  const entitlements = useMemo<Entitlements | null>(() => {
    if (!tenant || !user) return null;

    const systems = resolveSystems(tenant.systems);
    const contactsSource = resolveContactsSource(tenant.contactsSource);
    const leadsSource = resolveLeadsSource(tenant.leadsSource);
    const callsSource = resolveCallsSource(tenant.callsSource);

    // Los seis datos del plan entran juntos y salen cruzados. Antes las
    // capacidades y los campos se resolvían acá, cada uno por su lado, y el
    // cruce lo hacía —o se lo olvidaba— cada superficie. Ver `resolveSurfaces`.
    //
    // La fuente de Leads es el sexto y entra por lo mismo: con el modelo de
    // personas, mover de etapa **es** el tablero, así que decide si Leads se
    // ofrece como destino. Desde que se fue el interruptor Tabla/Kanban, sin
    // tablero no hay pantalla que ofrecer ni a dónde caer.
    const surfaces = resolveSurfaces(
      tenant.enabledModules,
      tenant.hiddenNavModules,
      tenant.hiddenLeadTabs,
      tenant.enabledCapabilities,
      tenant.enabledLeadFields,
      leadsSource,
    );
    const { modules, capabilities } = surfaces;

    // Lo que el servidor contestó, tal cual. Sin sesión resuelta no hay
    // permisos: el conjunto vacío es la respuesta segura —no dibuja ningún
    // control— y es la misma que da una cuenta a la que le revocaron todo.
    const permisos = new Set<Permiso>((permisosDeLaSesion?.permisos ?? []) as Permiso[]);
    const alcanceDePermisos: AlcanceDePermisos = {
      otorgables: new Set<Permiso>((permisosDeLaSesion?.otorgables ?? []) as Permiso[]),
      disponibles: new Set<Permiso>((permisosDeLaSesion?.disponibles ?? []) as Permiso[]),
    };

    return {
      ...surfaces,
      tenant,
      user,
      systems,
      platformRole,
      permisos,
      puede: (permiso) => permisos.has(permiso),
      alcanceDePermisos,
      contactsSource,
      leadsSource,
      callsSource,
      hasModule: (id) => modules.has(id),
      hasSystem: (id) => systems[id],
      // El descuento por módulo apagado ya está hecho en `resolveCapabilities`,
      // que es donde vive el mapa de dueños. Acá era una segunda copia de esa
      // regla, y una copia es un lugar donde quedarse viejo: las cinco llaves de
      // `personas.*` no tenían dueño y este `Map` no podía saberlo.
      can: (capability) => capabilities.has(capability),
    };
  }, [tenant, user, platformRole, permisosDeLaSesion]);

  // Y se deja anotado. Sin esto la sesión funciona pero el navegador sigue
  // guardando una organización muerta: la próxima visita paga el mismo rodeo, y
  // cualquiera que lea la elección directamente —el cierre de sesión, una
  // pestaña nueva— sigue leyendo la vieja. Nunca se escribe `null` acá: quedarse
  // sin organización es lo que hace el cierre de sesión, y borrar desde este
  // efecto pisaría la elección de alguien mientras una respuesta va en camino.
  useEffect(() => {
    if (needsTenantSync(tenantId, activeTenantId)) setTenantId(activeTenantId);
  }, [tenantId, activeTenantId, setTenantId]);

  const selectTenant = useCallback((next: string) => setTenantId(next), [setTenantId]);

  // Everything this browser knows about who was signed in, dropped at once.
  // Clearing the local choice first is deliberate: whatever the server says,
  // this browser is done, and a failing sign-out must not strand someone in a
  // session they asked to leave.
  const forget = useCallback(() => {
    setTenantId(null);
    forgetSession(queryClient);
  }, [setTenantId, queryClient]);

  const signOut = useCallback(() => {
    forget();
    // Again once the round-trip is over, win or lose: the cookie only dies when
    // the server says so, and a session query that resolved in the meantime
    // would refill the cache with the person who just left.
    void api.session
      .signOut()
      .catch(() => undefined)
      .finally(forget);
  }, [forget]);

  // The same cleanup under the other trigger: the cookie lasts an hour, and
  // until now only the session query noticed it expire. Every other screen
  // turned the 401 into an error panel over its own table.
  useEffect(() => onUnauthorized(forget), [forget]);

  const { refetch: refetchSession } = sessionQuery;
  const retry = useCallback(() => {
    void refetchSession();
  }, [refetchSession]);

  const status = sessionStatus({
    failed: sessionQuery.isError,
    pending: sessionQuery.isPending,
    entitled: entitlements !== null,
    tenants,
    active: activeTenantId,
  });

  const session = useMemo<Session>(
    () => ({ status, tenants, tenantId, selectTenant, signOut, retry }),
    [status, tenants, tenantId, selectTenant, signOut, retry],
  );

  return (
    <SessionContext.Provider value={session}>
      <EntitlementsContext.Provider value={entitlements}>{children}</EntitlementsContext.Provider>
    </SessionContext.Provider>
  );
}

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession() necesita un <TenantProvider> por encima.');
  return value;
}

/**
 * The only way to ask what this client bought. Views never read the tenant
 * object directly, which is what keeps the gates in one place.
 */
export function useEntitlements(): Entitlements {
  const value = useContext(EntitlementsContext);
  if (!value) throw new Error('useEntitlements() sólo funciona con una sesión activa.');
  return value;
}

/** Si quien mira puede cambiar el trabajo del día, con la misma respuesta que el servidor (`permisos.ts`). */
export function usePuedeEscribir(): boolean {
  const { user, platformRole } = useEntitlements();
  return puedeEscribir(user.role, platformRole);
}

/** Si quien mira administra la organización, con la misma respuesta que el servidor (`permisos.ts`). */
export function useAdministra(): boolean {
  const { user, platformRole } = useEntitlements();
  return puedeAdministrar(user.role, platformRole);
}

/** Quien mira, en la forma en que lo preguntan las reglas de `permisos.ts` (`accionesSobreNota`). */
export function useQuienMira(): QuienMira {
  const { user, platformRole } = useEntitlements();
  return useMemo(
    () => ({ id: user.id, role: user.role, platformRole }),
    [user.id, user.role, platformRole],
  );
}

/** Where a module is turned back on; the module owns everything under its path. */
const MODULES_SECTION_HREF = `${MODULES.settings.path}/modules`;

/**
 * Route guard for a module. It renders the "sin acceso" screen instead of
 * redirecting on purpose: the URL has to survive so it can be pasted to whoever
 * administers the plan.
 *
 * It keeps the page header the enabled screens have — losing it made the two
 * states of the same route look like two different products — and offers the
 * switch to whoever can actually flip it.
 */
export function RequireModule({ id, children }: { id: ModuleId; children: ReactNode }) {
  const { hasModule, sinTablero, user } = useEntitlements();
  if (hasModule(id)) {
    // Leads sin tablero es un módulo encendido que no tiene pantalla que
    // dibujar, así que se contesta acá y no adentro: dejarlo entrar sería la
    // pantalla de Leads explicando por qué no hay Leads, que es el cartel que
    // §18 prohíbe. La causa se dice, porque son dos y se arreglan distinto.
    if (id === 'leads' && sinTablero !== null) return <LeadsSinTablero motivo={sinTablero} />;
    return <>{children}</>;
  }

  const canTurnOn = hasModule('settings') && user.role === 'admin';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader title={MODULES[id].label} description={MODULES[id].description} />
      <NoAccessState
        moduleLabel={MODULES[id].label}
        description={
          canTurnOn
            ? 'Este módulo está apagado para tu organización. Puedes encenderlo tú mismo en Módulos; no se perdió ningún dato.'
            : 'Este módulo está apagado para tu organización. Quien administra la cuenta puede encenderlo desde Configuración → Módulos.'
        }
        {...(canTurnOn
          ? {
              primaryAction: (
                <Button size="sm" asChild>
                  <Link to={MODULES_SECTION_HREF}>Ir a Módulos</Link>
                </Button>
              ),
            }
          : {})}
      />
    </div>
  );
}

/**
 * Leads encendido, tablero imposible: la misma respuesta que un módulo apagado.
 *
 * Hasta el 2026-09-08 esto no hacía falta, porque `leads.kanban` apagado no
 * cerraba la pantalla sino que la dejaba caer a la tabla: la capacidad vetaba
 * **el tablero**, no el módulo (`hay_kanban` en `backend/api/v2/access.py`). Sin
 * tabla eso dejó de ser cierto, y la organización entraba a Leads y encontraba
 * un cartel que además le hablaba de Contactos —un módulo que puede no tener— y
 * le negaba de paso crear, importar y exportar.
 *
 * Así que se corta antes, y se dicen los DOS motivos por separado porque no se
 * arreglan igual: la capacidad es una llave que se vuelve a encender en Módulos
 * —y quien la enciende somos nosotros, la sección es de plataforma—, y el campo
 * `stage` no se enciende desde ninguna pantalla (docs/DECISIONES.md §8).
 *
 * Que falte el embudo o sus etapas NO llega hasta acá: eso es configuración a
 * medio hacer, no una decisión de producto, y lo contesta la pantalla —que para
 * entonces ya pidió los embudos y sabe cuál de las dos cosas falta—.
 */
function LeadsSinTablero({ motivo }: { motivo: MotivoSinTablero }) {
  const { platformRole } = useEntitlements();
  // Sólo a quien de verdad puede: la sección Módulos es `platformOnly`, así que
  // ofrecerle el botón al Administrador del cliente sería mandarlo a una puerta
  // cerrada. Los dos roles nuestros la abren — encender un módulo es configurar.
  const puedeEncender = motivo === 'capacidad' && platformRole !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader title={MODULES.leads.label} description={MODULES.leads.description} />
      <NoAccessState
        moduleLabel={MODULES.leads.label}
        description={
          motivo === 'capacidad'
            ? 'Leads es el embudo por etapas, y el tablero está apagado para tu organización. Se enciende en Configuración → Módulos; no se perdió ningún dato.'
            : 'Leads es el embudo por etapas, y esta organización no tiene el campo «etapa». La configuración la hacemos nosotros: escríbenos y lo activamos.'
        }
        {...(puedeEncender
          ? {
              primaryAction: (
                <Button size="sm" asChild>
                  <Link to={MODULES_SECTION_HREF}>Ir a Módulos</Link>
                </Button>
              ),
            }
          : {})}
      />
    </div>
  );
}
